import { Container } from '@/components/layout/Container';
import Styled from '@/components/TextListTable/TextListTable.styled';
import { FC, ReactNode } from 'react';

export type TextListTable = {
  eyebrow?: string;
  description?: ReactNode;
  columns: {
    id: string | number;
    title: string;
    rows: {
      id: string | number;
      icon: string;
      text: string;
    }[];
    footer?: ReactNode;
    backgroundColor?: string;
  }[];
  joinColumns: boolean;
  backgroundColor?: string;
  onBackground?: boolean;
};

export interface TextListTableProps {
  heading?: string;
  description?: ReactNode;
  tables: TextListTable[];
}

const Table: FC<TextListTable> = (table) => {
  const onBackground = table.onBackground || !!table.backgroundColor;

  return (
    <Styled.TableWrapper backgroundColor={table.backgroundColor}>
      <Container>
        {table.eyebrow && <Styled.Eyebrow onBackground={onBackground}>{table.eyebrow}</Styled.Eyebrow>}

        {table.description && <Styled.Description>{table.description}</Styled.Description>}

        <Styled.Table>
          {table.columns.map((column) => {
            return (
              <Styled.Column
                key={column.id}
                backgroundColor={column.backgroundColor}
                onBackground={onBackground}
                joinColumns={table.joinColumns}
                columnCount={table.columns.length}
              >
                <Styled.ColumnHeading>{column.title}</Styled.ColumnHeading>

                <Styled.List>
                  {column.rows.map((row) => {
                    const Icon = () => {
                      switch (row.icon) {
                        case 'x':
                          return (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM8.20711 6.79289C7.81658 6.40237 7.18342 6.40237 6.79289 6.79289C6.40237 7.18342 6.40237 7.81658 6.79289 8.20711L10.5858 12L6.79289 15.7929C6.40237 16.1834 6.40237 16.8166 6.79289 17.2071C7.18342 17.5976 7.81658 17.5976 8.20711 17.2071L12 13.4142L15.7927 17.2069C16.1832 17.5974 16.8164 17.5974 17.2069 17.2069C17.5974 16.8164 17.5974 16.1832 17.2069 15.7927L13.4142 12L17.2069 8.20729C17.5974 7.81677 17.5974 7.1836 17.2069 6.79308C16.8164 6.40255 16.1832 6.40255 15.7927 6.79308L12 10.5858L8.20711 6.79289Z"
                                fill="#001B38"
                              />
                            </svg>
                          );
                        case 'checkmark':
                          return (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM17.9571 9.70711C18.3476 9.31658 18.3476 8.68342 17.9571 8.29289C17.5666 7.90237 16.9334 7.90237 16.5429 8.29289L10.4559 14.3799L7.45711 11.3811C7.06658 10.9906 6.43342 10.9906 6.04289 11.3811C5.65237 11.7717 5.65237 12.4048 6.04289 12.7953L9.74878 16.5012C9.93631 16.6888 10.1907 16.7941 10.4559 16.7941C10.7211 16.7941 10.9755 16.6888 11.163 16.5012L17.9571 9.70711Z"
                                fill="#001B38"
                              />
                            </svg>
                          );
                        case 'star':
                          return (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM13.8532 9.00498L12.8967 7.06699C12.5299 6.32374 11.4701 6.32374 11.1033 7.06699L10.1468 9.00498C10.0011 9.30013 9.71958 9.50469 9.39387 9.55202L7.25516 9.86279C6.43494 9.98198 6.10743 10.99 6.70095 11.5685L8.24853 13.077C8.48422 13.3067 8.59177 13.6377 8.53613 13.9621L8.17079 16.0922C8.03068 16.9091 8.88812 17.5321 9.62174 17.1464L11.5347 16.1407C11.826 15.9875 12.174 15.9875 12.4653 16.1407L14.3783 17.1464C15.1119 17.5321 15.9693 16.9091 15.8292 16.0922L15.4639 13.9621C15.4082 13.6377 15.5158 13.3067 15.7515 13.077L17.2991 11.5685C17.8926 10.99 17.5651 9.98198 16.7448 9.86279L14.6061 9.55202C14.2804 9.50469 13.9989 9.30013 13.8532 9.00498Z"
                                fill="#001B38"
                              />
                            </svg>
                          );
                        default:
                          return null;
                      }
                    };

                    return (
                      <Styled.ListItem key={row.id}>
                        <Icon />
                        <p>{row.text}</p>
                      </Styled.ListItem>
                    );
                  })}
                </Styled.List>

                {column.footer && <Styled.ColumnFooter>{column.footer}</Styled.ColumnFooter>}
              </Styled.Column>
            );
          })}
        </Styled.Table>
      </Container>
    </Styled.TableWrapper>
  );
};

const TextListTable: FC<TextListTableProps> = (props) => {
  return (
    <section>
      <Styled.HeaderWrapper backgroundColor={props.tables[0].backgroundColor}>
        {(props.heading || props.description) && (
          <Container>
            <Styled.Header>
              {props.heading && <Styled.Heading>{props.heading}</Styled.Heading>}
              {props.description}
            </Styled.Header>
          </Container>
        )}

        {props.tables.length > 0 && <Table {...props.tables[0]} backgroundColor={undefined} onBackground />}
      </Styled.HeaderWrapper>

      {props.tables.slice(1).map((table, i) => (
        <Table key={`text-list-table-${i}`} {...table} />
      ))}
    </section>
  );
};

export default TextListTable;
